import { FC, ReactNode } from "react";
import { useIntl } from "react-intl";

import { Box, Typography } from "@mui/material";

import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { formatTrendTooltipDate } from "../../../../../../utils";
import { formatMeasurementLabel } from "../../../../../../utils/measurement-label";
import { useAlarmPaletteResolver } from "../../../../../../hooks/use-alarm-color";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import type { IMultiAnalysisDataMark } from "../../types";

interface IDataMarkTooltipProps {
  mark: IMultiAnalysisDataMark;
}

export const DataMarkTooltipContent: FC<IDataMarkTooltipProps> = ({ mark }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const formattedValue = formatMeasurementNumber(mark.value, mark.units, profile.interfaceSettings.dataFormat, {
    fallbackDigits: 2,
  });

  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#202534" }}>
        {formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" })}: {mark.pointName}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#4f5d75" }}>
        {formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" })}:{" "}
        {formatMeasurementLabel(mark.measureName, mark.measureDescription)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#4f5d75" }}>
        {formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" })}: {formatTrendTooltipDate(mark.date)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#4f5d75" }}>
        {formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.value" })}: {formattedValue}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: 0.5,
            bgcolor: resolveAlarmColor(mark.alarmStatus),
          }}
        />
        <Typography sx={{ fontSize: 11, color: "#6a7284" }}>
          {formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.alarmStatus" })}
        </Typography>
      </Box>
    </Box>
  );
};

export const MarkTooltip: FC<{
  mark: IMultiAnalysisDataMark;
  renderMarkTooltip?: (mark: IMultiAnalysisDataMark) => ReactNode | null;
}> = ({ mark, renderMarkTooltip }) => {
  const custom = renderMarkTooltip?.(mark);
  return custom ? <>{custom}</> : <DataMarkTooltipContent mark={mark} />;
};
